import React, { useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * Renders the operator-configured captcha widget.
 *
 * Loads the right vendor script on demand (hCaptcha / reCAPTCHA v2 / v3 /
 * Cloudflare Turnstile) and wires its callback through to the parent.
 * Originally lived inside LoginPage; lifted here so the waitlist form
 * (and any other public form we add later — onboarding, password-reset,
 * etc.) can reuse the exact same auth-shape and we don't drift between
 * two implementations.
 *
 * Props:
 *   provider, siteKey  — from GET /api/auth/captcha-config
 *   onVerify(token)    — called when the widget hands us a token
 *   onExpire()         — called when the token expires (≈2 min)
 */
const CaptchaWidget = ({ provider, siteKey, onVerify, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const renderAttempted = useRef(false);

  const renderCaptcha = useCallback(() => {
    if (!containerRef.current || renderAttempted.current) return;

    if (provider === 'RECAPTCHA_V2') {
      if (window.grecaptcha && window.grecaptcha.render) {
        renderAttempted.current = true;
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': onExpire,
        });
      }
    } else if (provider === 'RECAPTCHA_V3') {
      // V3 is invisible — token is fetched on submit by the parent.
      renderAttempted.current = true;
    } else if (provider === 'HCAPTCHA') {
      if (window.hcaptcha && window.hcaptcha.render) {
        renderAttempted.current = true;
        widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': onExpire,
        });
      }
    } else if (provider === 'TURNSTILE') {
      if (window.turnstile && window.turnstile.render) {
        renderAttempted.current = true;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': onExpire,
        });
      }
    }
  }, [provider, siteKey, onVerify, onExpire]);

  useEffect(() => {
    renderAttempted.current = false;

    // Hide the floating reCAPTCHA badge globally; Google allows this when
    // text attribution is shown on the form.
    if (!document.getElementById('captcha-badge-hide')) {
      const style = document.createElement('style');
      style.id = 'captcha-badge-hide';
      style.textContent = '.grecaptcha-badge { visibility: hidden !important; }';
      document.head.appendChild(style);
    }

    let scriptSrc = null;
    const scriptId = 'captcha-script';
    if (provider === 'RECAPTCHA_V2')      scriptSrc = 'https://www.google.com/recaptcha/api.js?render=explicit';
    else if (provider === 'RECAPTCHA_V3') scriptSrc = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    else if (provider === 'HCAPTCHA')     scriptSrc = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    else if (provider === 'TURNSTILE')    scriptSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

    if (!scriptSrc) return;

    const existing = document.getElementById(scriptId);
    if (existing) {
      // Script already in DOM (login page mounted us before, etc.) — just
      // try to render. The polling fallback covers the case where the
      // vendor JS isn't done evaluating yet.
      renderCaptcha();
      const interval = setInterval(() => {
        renderCaptcha();
        if (renderAttempted.current) clearInterval(interval);
      }, 200);
      const stop = setTimeout(() => clearInterval(interval), 10000);
      return () => { clearInterval(interval); clearTimeout(stop); };
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const interval = setInterval(() => {
        renderCaptcha();
        if (renderAttempted.current) clearInterval(interval);
      }, 200);
      setTimeout(() => clearInterval(interval), 10000);
    };
    document.head.appendChild(script);
    // Note: we deliberately don't remove the script on unmount — other
    // forms on the page (login, onboarding) likely use the same vendor
    // and pulling the script would re-trigger the load.
  }, [provider, siteKey, renderCaptcha]);

  if (provider === 'RECAPTCHA_V3') return null;
  return <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }} />;
};

export default CaptchaWidget;
